import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { provideRouter, Router } from '@angular/router'

import * as Sentry from '@sentry/angular'
import { NgxCsvParserModule } from 'ngx-csv-parser'
import { MessageService } from 'primeng/api'
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { TooltipStyle } from 'primeng/tooltip'

import { ApiModule } from '@ti/core/api/api.module'
import { AuthModule } from '@ti/core/auth/auth.module'
import { BrowserPushClient, TspProfile } from '@ti/core/auth/services'
import { provideFirebaseApp } from '@ti/core/auth/utils/provide-fb-app'
import { AppCommonModule } from '@ti/core/common/common.module'
import {
  BrowserFileSaver,
  GlobalErrorHandler,
  provideBrowserNetworkStatusProvider,
  WhiteLabelSettingsProvider,
} from '@ti/core/common/services'
import { provideBrowserClipboard } from '@ti/core/common/services/clipboard'
import { provideBrowserExternalLinkOpener } from '@ti/core/common/services/external-link-opener.service'
import { BrowserScreenFacade } from '@ti/core/common/services/screen.facade'
import { provideBrowserServiceWorker } from '@ti/core/common/services/update.manager'
import { CONFIG } from '@ti/core/contracts/config'
import { FILE_SAVER } from '@ti/core/contracts/file-saver'
import { I18NConfigProvider } from '@ti/core/contracts/i18n'
import { PROFILE_AUTO_LOAD } from '@ti/core/contracts/profile'
import { PUSH_CLIENT } from '@ti/core/contracts/push'
import { SCREEN_FACADE } from '@ti/core/contracts/screen.facade'
import { provideAppVersion } from '@ti/core/contracts/version'
import { providePrimeNgPreset } from '@ti/core/themes/services'
import { provideLanguageInitializer, provideTranslationsLoader } from '@ti/core/ui-kit/i18n'
import { DIALOG_SERVICE_PROVIDER, provideDialogFacade } from '@ti/core/ui-kit/providers/globals'
import { UiKitModule } from '@ti/core/ui-kit/ui-kit.module'
import { GroupsOptionsProvider } from '@ti/shared/groups/services'
import { UserModule } from '@ti/shared/user/user.module'

import environment from '../env'
import { APP_VERSION as version } from '../env/version'
import { AppInitializer } from './app.initializer'
import { BillingDashboardModel } from './billing/models/billing-dashboard.model'
import { BillingUsageReportModel } from './billing/models/billing-usage-report.model'
import { AUTH_PROVIDERS } from './common/services/auth'
import { DisplayFleetsDataProvider } from './common/services/display-fleets-data.provider'
import { LANG_PROVIDERS, TspAdminI18NConfigProvider } from './common/services/lang'
import { provideTspRealtimeClient } from './common/services/realtime.client'
import { TspAdminWhiteLabelSettingsProvider } from './common/services/white-label-settings.provider'
import { CommandCommonProvider } from './devices/models/commands-common.provider'
import { CustomSensorsDataProvider } from './devices/models/custom-sensors-data.provider'
import { DevicesTypesDataProvider } from './devices/models/devices-types-data.provider'
import { CountyOptionsProvider } from './fleet/models/country-options.provider'
import { PricingPlansProvider } from './fleet/models/pricing-plans.provider'
import { provideTspAlerts } from './fleet/utils/provide-tsp-alerts'
import { McpAuthContext } from './mcp/bootstrap/mcp-auth-context'
import APP_ROUTES from './tsp-admin-app.routes'
import { provideTspUserManager } from './users/services/user.manager'
import { DomainAssociationProvider } from './white-label/services/domain-association.provider'
import { EmailDomainAssociationProvider } from './white-label/services/email-domain-association.provider'

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES),

    provideBrowserServiceWorker('ngsw-worker.js', { enabled: environment.envType !== 'local' }),
    importProvidersFrom(NgxCsvParserModule),
    importProvidersFrom(ApiModule),
    importProvidersFrom(AppCommonModule),
    importProvidersFrom(AuthModule),
    importProvidersFrom(UiKitModule),
    importProvidersFrom(UserModule),

    provideAnimationsAsync(),
    TspProfile,
    DIALOG_SERVICE_PROVIDER,
    MessageService,
    TooltipStyle,
    BillingUsageReportModel,
    BillingDashboardModel,
    provideLanguageInitializer(),
    provideTranslationsLoader(['tsp', 'shared']),
    DevicesTypesDataProvider,
    CustomSensorsDataProvider,
    DisplayFleetsDataProvider,
    CommandCommonProvider,
    PricingPlansProvider,
    CountyOptionsProvider,
    DomainAssociationProvider,
    EmailDomainAssociationProvider,
    DynamicDialogRef,
    DynamicDialogConfig,
    { provide: CONFIG, useValue: environment },
    { provide: FILE_SAVER, useClass: BrowserFileSaver },
    { provide: PUSH_CLIENT, useClass: BrowserPushClient },
    { provide: SCREEN_FACADE, useClass: BrowserScreenFacade },
    { provide: ErrorHandler, useFactory: () => new GlobalErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => void inject(Sentry.TraceService)), // Init trace service
    ...AUTH_PROVIDERS,
    ...LANG_PROVIDERS,
    { provide: I18NConfigProvider, useClass: TspAdminI18NConfigProvider },
    { provide: PROFILE_AUTO_LOAD, useFactory: () => inject(McpAuthContext).profileEnabled$ },
    { provide: WhiteLabelSettingsProvider, useClass: TspAdminWhiteLabelSettingsProvider },
    { provide: GroupsOptionsProvider, useValue: undefined },
    provideAppVersion(version),
    provideFirebaseApp(),
    provideTspRealtimeClient(),
    provideTspAlerts(),
    provideTspUserManager(),
    provideDialogFacade(),
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNgPreset(),
    provideBrowserClipboard(),
    provideBrowserExternalLinkOpener(),
    provideBrowserNetworkStatusProvider(),
    provideAppInitializer(() => {
      const initializer = inject(AppInitializer)
      return initializer.init()
    }),
  ],
}
